import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { getLevelColor, getLevelLetter } from "../utils/levelColors";

const SERIE_ORDER = ["A", "B", "C"];
const SERIE_FALLBACK = ["#38d39f", "#66b8ff", "#c4b5fd"];

const getLinkNodeId = (endpoint) => (typeof endpoint === "object" ? endpoint?.id : endpoint);

const formatPower = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "0";
  return parsed.toLocaleString("vi-VN", { maximumFractionDigits: 0 });
};

const makeStarTexture = () => {
  const canvas = document.createElement("canvas");
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext("2d");
  const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  gradient.addColorStop(0, "rgba(255,255,255,1)");
  gradient.addColorStop(0.28, "rgba(255,255,255,0.85)");
  gradient.addColorStop(0.55, "rgba(170,210,255,0.28)");
  gradient.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 64, 64);
  const texture = new THREE.CanvasTexture(canvas);
  if ("colorSpace" in texture) texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
};

/** Soft radial disc for team corona + player sprites (hybrid look). */
const makeSoftDiscTexture = () => {
  const canvas = document.createElement("canvas");
  canvas.width = 128;
  canvas.height = 128;
  const ctx = canvas.getContext("2d");
  const gradient = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
  gradient.addColorStop(0, "rgba(255,255,255,1)");
  gradient.addColorStop(0.18, "rgba(255,255,255,0.92)");
  gradient.addColorStop(0.42, "rgba(210,230,255,0.45)");
  gradient.addColorStop(0.68, "rgba(120,170,255,0.12)");
  gradient.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 128, 128);
  const texture = new THREE.CanvasTexture(canvas);
  if ("colorSpace" in texture) texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
};

const makeRingTexture = () => {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 32;
  const ctx = canvas.getContext("2d");
  const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
  gradient.addColorStop(0, "rgba(255,255,255,0)");
  gradient.addColorStop(0.28, "rgba(255,255,255,0.2)");
  gradient.addColorStop(0.5, "rgba(255,255,255,1)");
  gradient.addColorStop(0.72, "rgba(255,255,255,0.2)");
  gradient.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  return texture;
};

const hash01 = (value) => {
  const text = String(value || "");
  let hash = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return ((hash >>> 0) % 100000) / 100000;
};

const makeTeamPlanetTexture = (teamId, serieColor) => {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 128;
  const ctx = canvas.getContext("2d");
  const seed = hash01(teamId);
  const base = new THREE.Color(serieColor);
  const hsl = {};
  base.getHSL(hsl);
  const hue = (hsl.h + (seed - 0.5) * 0.28 + 1) % 1;
  const ocean = new THREE.Color().setHSL(hue, 0.62 + hash01(`${teamId}-ocean`) * 0.2, 0.24 + hash01(`${teamId}-light`) * 0.16);
  const land = new THREE.Color().setHSL((hue + 0.08 + hash01(`${teamId}-land`) * 0.15) % 1, 0.46 + hash01(`${teamId}-sat`) * 0.3, 0.34 + hash01(`${teamId}-landlight`) * 0.2);
  const cloud = new THREE.Color().setHSL((hue + 0.5) % 1, 0.24, 0.76);
  ctx.fillStyle = `#${ocean.getHexString()}`;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  for (let index = 0; index < 9; index += 1) {
    const x = hash01(`${teamId}-continent-x-${index}`) * canvas.width;
    const y = hash01(`${teamId}-continent-y-${index}`) * canvas.height;
    const width = 28 + hash01(`${teamId}-continent-w-${index}`) * 74;
    const height = 10 + hash01(`${teamId}-continent-h-${index}`) * 30;
    ctx.fillStyle = `#${land.clone().offsetHSL((index - 4) * 0.012, 0, (index % 3 - 1) * 0.05).getHexString()}`;
    ctx.beginPath();
    ctx.ellipse(x, y, width, height, (hash01(`${teamId}-continent-r-${index}`) - 0.5) * 0.8, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 0.22;
  ctx.strokeStyle = `#${cloud.getHexString()}`;
  ctx.lineWidth = 3;
  for (let index = 0; index < 5; index += 1) {
    const y = 12 + index * 26 + hash01(`${teamId}-cloud-${index}`) * 12;
    ctx.beginPath();
    ctx.bezierCurveTo(0, y, 64, y - 16, 172, y + 16, canvas.width, y - 4);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  if ("colorSpace" in texture) texture.colorSpace = THREE.SRGBColorSpace;
  return { texture, color: ocean };
};

const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

const makeLabelSprite = (text, options = {}) => {
  const { scale = 1, sub = "" } = options;
  const canvas = document.createElement("canvas");
  canvas.width = 320;
  canvas.height = sub ? 96 : 64;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "rgba(2,6,23,0.58)";
  const h = sub ? 72 : 44;
  const y = sub ? 12 : 10;
  const r = 12;
  const x = 16;
  const w = canvas.width - 32;
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "#f8fafc";
  ctx.font = "700 26px Segoe UI, system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(String(text || "").slice(0, 28), canvas.width / 2, sub ? 38 : 32);
  if (sub) {
    ctx.fillStyle = "rgba(158,243,207,0.95)";
    ctx.font = "700 18px Segoe UI, system-ui, sans-serif";
    ctx.fillText(String(sub).slice(0, 24), canvas.width / 2, 64);
  }
  const texture = new THREE.CanvasTexture(canvas);
  if ("colorSpace" in texture) texture.colorSpace = THREE.SRGBColorSpace;
  const material = new THREE.SpriteMaterial({ map: texture, transparent: true, depthWrite: false });
  const sprite = new THREE.Sprite(material);
  sprite.scale.set(56 * scale, (sub ? 17 : 11) * scale, 1);
  return sprite;
};

const buildBackgroundGalaxy = (starTexture, armColors) => {
  const count = 12000;
  const radius = 780;
  const branches = Math.max(3, armColors.length);
  const spin = 1.12;
  const randomness = 0.4;
  const randomnessPower = 2.35;
  const inside = new THREE.Color("#ffd9a0");
  const outside = new THREE.Color("#3a5cff");
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);

  for (let i = 0; i < count; i += 1) {
    const i3 = i * 3;
    const r = Math.random() * radius;
    const branchAngle = ((i % branches) / branches) * Math.PI * 2;
    const spinAngle = r * spin * 0.01;
    const rnd = () =>
      Math.pow(Math.random(), randomnessPower) * (Math.random() < 0.5 ? 1 : -1) * randomness * r;
    positions[i3] = Math.cos(branchAngle + spinAngle) * r + rnd();
    positions[i3 + 1] = rnd() * 0.26;
    positions[i3 + 2] = Math.sin(branchAngle + spinAngle) * r + rnd();

    const mixed = inside.clone().lerp(outside, r / radius);
    const tint = armColors[i % armColors.length] || outside;
    mixed.lerp(tint, 0.2);
    colors[i3] = mixed.r;
    colors[i3 + 1] = mixed.g;
    colors[i3 + 2] = mixed.b;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  const material = new THREE.PointsMaterial({
    size: 3.1,
    map: starTexture,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    vertexColors: true,
    sizeAttenuation: true,
    opacity: 0.88,
  });
  const points = new THREE.Points(geometry, material);
  points.name = "bg-galaxy";
  return points;
};

const buildDustLane = (starTexture) => {
  const count = 2200;
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  for (let i = 0; i < count; i += 1) {
    const i3 = i * 3;
    const radius = 90 + Math.pow(Math.random(), 0.58) * 960;
    const theta = Math.random() * Math.PI * 2;
    const elevation = Math.asin((Math.random() * 2 - 1) * 0.72);
    positions[i3] = Math.cos(elevation) * Math.cos(theta) * radius;
    positions[i3 + 1] = Math.sin(elevation) * radius;
    positions[i3 + 2] = Math.cos(elevation) * Math.sin(theta) * radius;
    const depth = Math.min(1, radius / 1050);
    const c = 0.1 + (1 - depth) * 0.14 + Math.random() * 0.08;
    colors[i3] = c * 0.76;
    colors[i3 + 1] = c * 0.96;
    colors[i3 + 2] = c * 1.32;
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  const particles = new THREE.Points(
    geometry,
    new THREE.PointsMaterial({
      size: 4.2,
      map: starTexture,
      transparent: true,
      depthWrite: false,
      vertexColors: true,
      opacity: 0.48,
      sizeAttenuation: true,
    }),
  );
  particles.name = "volume-particles";
  return particles;
};

const buildFarStars = (starTexture) => {
  const count = 2200;
  const positions = new Float32Array(count * 3);
  for (let i = 0; i < count; i += 1) {
    const i3 = i * 3;
    const r = 1500 + Math.random() * 1100;
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    positions[i3] = r * Math.sin(phi) * Math.cos(theta);
    positions[i3 + 1] = r * Math.sin(phi) * Math.sin(theta);
    positions[i3 + 2] = r * Math.cos(phi);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  return new THREE.Points(
    geometry,
    new THREE.PointsMaterial({
      size: 1.5,
      map: starTexture,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      color: 0xffffff,
      opacity: 0.72,
    }),
  );
};

const resolveSerieKey = (node) => {
  const fromName = getLevelLetter(node?.graph_level_name || node?.name || "");
  if (SERIE_ORDER.includes(fromName)) return fromName;
  const id = String(node?.graph_level_id || "");
  if (id) {
    const letter = getLevelLetter(getLevelColor(node?.graph_level_name).solid) || fromName;
    if (SERIE_ORDER.includes(letter)) return letter;
  }
  return fromName || "A";
};

const placeOnArm = (teamId, index, total, armIndex, armCount) => {
  const branchAngle = (armIndex / Math.max(1, armCount)) * Math.PI * 2;
  const t = (index + 1) / (total + 1);
  const r = 150 + t * 520;
  const seed = hash01(teamId);
  const spin = r * 0.0115;
  const angle = branchAngle + spin + index * 0.27 + (seed - 0.5) * 0.16;
  const depthLayer = Math.floor(seed * 3) - 1;
  const y = depthLayer * 82 + Math.sin(index * 1.55 + armIndex + seed * Math.PI) * 34;
  const zScale = 0.68 + seed * 0.56;
  return new THREE.Vector3(Math.cos(angle) * r, y, Math.sin(angle) * r * zScale);
};

const GalaxyHomeScene = forwardRef(function GalaxyHomeScene(
  {
    graph,
    width,
    height,
    showScheduleLinks = false,
    focusSerieId = "",
    selectedNodeId = "",
    focusNodeIds = null,
    onNodeClick,
    onBackgroundClick,
  },
  ref,
) {
  const mountRef = useRef(null);
  const stateRef = useRef(null);
  const onNodeClickRef = useRef(onNodeClick);
  const onBackgroundClickRef = useRef(onBackgroundClick);
  const graphKey = useMemo(() => {
    const nodes = graph?.nodes || [];
    const links = graph?.links || [];
    return `${nodes.length}:${links.length}:${nodes[0]?.id || ""}:${graph?.season_id || ""}`;
  }, [graph]);

  useEffect(() => {
    onNodeClickRef.current = onNodeClick;
  }, [onNodeClick]);

  useEffect(() => {
    onBackgroundClickRef.current = onBackgroundClick;
  }, [onBackgroundClick]);

  useImperativeHandle(ref, () => ({
    fitAll: (duration = 900) => stateRef.current?.fitAll?.(duration),
    focusNode: (node, duration = 1200) => stateRef.current?.focusNode?.(node, duration),
    focusTeamSystem: (teamNode, duration = 700) => stateRef.current?.focusTeamSystem?.(teamNode, duration),
    focusSerie: (serieKeyOrLevelId, duration = 1200) => stateRef.current?.focusSerie?.(serieKeyOrLevelId, duration),
    resetCamera: () => stateRef.current?.resetCamera?.(),
    setAutoRotate: (enabled) => stateRef.current?.setAutoRotate?.(enabled),
    clearFocus: () => stateRef.current?.clearFocus?.(),
    zoomBy: (factor = 0.8) => stateRef.current?.zoomBy?.(factor),
  }));

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    const isMobile = window.innerWidth < 768;
    const enableBloom = !isMobile && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const renderer = new THREE.WebGLRenderer({ antialias: !isMobile, alpha: false, powerPreference: "high-performance" });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, isMobile ? 1.5 : 2));
    renderer.setSize(width || mount.clientWidth || 800, height || mount.clientHeight || 600);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.setClearColor(0x01040c, 1);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x01040c, 0.0005);

    const camera = new THREE.PerspectiveCamera(
      55,
      Math.max(1, (width || mount.clientWidth || 800) / Math.max(1, height || mount.clientHeight || 600)),
      1,
      6000,
    );
    camera.position.set(0, 310, 1120);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = isMobile ? 0.1 : 0.06;
    controls.enablePan = true;
    controls.enableZoom = true;
    controls.enableRotate = true;
    controls.minDistance = 8;
    controls.maxDistance = 4200;
    controls.minPolarAngle = 0.05;
    controls.maxPolarAngle = Math.PI - 0.05;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.18;
    controls.target.set(0, 0, 0);
    controls.rotateSpeed = isMobile ? 1.05 : 0.75;
    controls.zoomSpeed = isMobile ? 1.4 : 1.1;
    controls.panSpeed = isMobile ? 0.85 : 0.55;
    controls.screenSpacePanning = true;
    // Mobile browsers often need explicit touch listeners for pinch/rotate.
    controls.touches = {
      ONE: THREE.TOUCH.ROTATE,
      TWO: THREE.TOUCH.DOLLY_PAN,
    };
    renderer.domElement.style.touchAction = "none";
    renderer.domElement.style.outline = "none";
    renderer.domElement.style.userSelect = "none";
    renderer.domElement.style.webkitUserSelect = "none";

    scene.add(new THREE.AmbientLight(0x6a7a9a, 0.55));
    const keyLight = new THREE.PointLight(0x9ef3cf, 1.15, 2800);
    keyLight.position.set(220, 320, 420);
    scene.add(keyLight);
    const fillLight = new THREE.PointLight(0x66b8ff, 0.65, 2800);
    fillLight.position.set(-420, -80, -240);
    scene.add(fillLight);

    const starTexture = makeStarTexture();
    const softDiscTexture = makeSoftDiscTexture();
    const ringTexture = makeRingTexture();
    const armColors = SERIE_FALLBACK.map((hex) => new THREE.Color(hex));
    const bgGalaxy = buildBackgroundGalaxy(starTexture, armColors);
    const dust = buildDustLane(starTexture);
    const farStars = buildFarStars(starTexture);
    const teamVisuals = [];
    scene.add(bgGalaxy);
    scene.add(dust);
    scene.add(farStars);

    const sun = new THREE.Mesh(
      new THREE.SphereGeometry(24, 32, 32),
      new THREE.MeshStandardMaterial({
        color: 0xffe6b0,
        emissive: 0xffc078,
        emissiveIntensity: 1.35,
        roughness: 0.25,
      }),
    );
    scene.add(sun);
    const sunGlow = new THREE.Mesh(
      new THREE.SphereGeometry(52, 24, 24),
      new THREE.MeshBasicMaterial({ color: 0xffb75e, transparent: true, opacity: 0.14, depthWrite: false }),
    );
    scene.add(sunGlow);

    const contentRoot = new THREE.Group();
    scene.add(contentRoot);
    const battleRoot = new THREE.Group();
    scene.add(battleRoot);

    let composer = null;
    let bloomPass = null;
    if (enableBloom) {
      composer = new EffectComposer(renderer);
      composer.addPass(new RenderPass(scene, camera));
      bloomPass = new UnrealBloomPass(
        new THREE.Vector2(width || mount.clientWidth || 800, height || mount.clientHeight || 600),
        0.48,
        0.55,
        0.2,
      );
      composer.addPass(bloomPass);
      composer.addPass(new OutputPass());
    }

    const pickables = [];
    const playerOrbits = [];
    const nodeMap = new Map();
    const serieCenters = new Map();
    let fly = null;
    let disposed = false;
    const clock = new THREE.Clock();
    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();

    const clearGroup = (group) => {
      while (group.children.length) {
        const child = group.children.pop();
        child.traverse?.((obj) => {
          if (obj.geometry) obj.geometry.dispose?.();
          if (obj.material) {
            if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose?.());
            else obj.material.dispose?.();
          }
        });
      }
    };

    const rebuildContent = (graphData) => {
      clearGroup(contentRoot);
      clearGroup(battleRoot);
      pickables.length = 0;
      playerOrbits.length = 0;
      teamVisuals.length = 0;
      nodeMap.clear();
      serieCenters.clear();

      const nodes = (graphData?.nodes || []).map((node) => ({ ...node }));
      const links = graphData?.links || [];
      const teams = nodes.filter((node) => node.type === "team");
      const players = nodes.filter((node) => node.type === "player");
      const levels = nodes.filter((node) => node.type === "level");

      const teamsBySerie = new Map();
      teams.forEach((team) => {
        const letter = resolveSerieKey(team);
        if (!teamsBySerie.has(letter)) teamsBySerie.set(letter, []);
        teamsBySerie.get(letter).push(team);
      });

      const serieKeys = SERIE_ORDER.filter((key) => teamsBySerie.has(key)).concat(
        [...teamsBySerie.keys()].filter((key) => !SERIE_ORDER.includes(key)),
      );
      const armCount = Math.max(3, serieKeys.length);

      serieKeys.forEach((serieKey, armIndex) => {
        const list = (teamsBySerie.get(serieKey) || []).sort(
          (a, b) => Number(b.strength || 0) - Number(a.strength || 0),
        );
        const colorHex = getLevelColor(serieKey === "A" || serieKey === "B" || serieKey === "C" ? `Serie ${serieKey}` : serieKey).solid
          || SERIE_FALLBACK[armIndex % SERIE_FALLBACK.length];
        const color = new THREE.Color(SERIE_FALLBACK[armIndex % SERIE_FALLBACK.length]);
        try {
          color.set(colorHex);
        } catch (err) {
          color.set(SERIE_FALLBACK[armIndex % SERIE_FALLBACK.length]);
        }

        const centerAccum = new THREE.Vector3();
        list.forEach((team, index) => {
          const pos = placeOnArm(team.id, index, list.length, armIndex, armCount);
          team.x = pos.x;
          team.y = pos.y;
          team.z = pos.z;
          centerAccum.add(pos);

          const radius = 6 + (Number(team.visual_size) || 0.5) * 14 + Math.min(8, Math.abs(Number(team.strength) || 0) * 0.12);
          const planet = makeTeamPlanetTexture(team.id, color);
          const core = new THREE.Mesh(
            new THREE.SphereGeometry(radius, isMobile ? 20 : 28, isMobile ? 20 : 28),
            new THREE.MeshStandardMaterial({
              color: "#ffffff",
              map: planet.texture,
              emissive: planet.color,
              emissiveMap: planet.texture,
              emissiveIntensity: 0.22,
              roughness: 0.62,
              metalness: 0.08,
              transparent: true,
              opacity: 1,
            }),
          );
          core.position.copy(pos);
          core.userData = { node: team, kind: "team" };
          contentRoot.add(core);
          pickables.push(core);

          const hitPad = new THREE.Mesh(
            new THREE.SphereGeometry(Math.max(radius * 2.8, 18), 12, 12),
            new THREE.MeshBasicMaterial({
              transparent: true,
              opacity: 0,
              depthWrite: false,
            }),
          );
          hitPad.position.copy(pos);
          hitPad.userData = { node: team, kind: "team" };
          contentRoot.add(hitPad);
          pickables.push(hitPad);

          // Soft corona billboard (replaces hard sphere glow).
          const corona = new THREE.Sprite(
            new THREE.SpriteMaterial({
              map: softDiscTexture,
              color,
              transparent: true,
              opacity: 0.55,
              depthWrite: false,
              blending: THREE.AdditiveBlending,
            }),
          );
          corona.position.copy(pos);
          const coronaSize = radius * 5.2;
          corona.scale.set(coronaSize, coronaSize, 1);
          contentRoot.add(corona);

          const ringMat = new THREE.MeshBasicMaterial({
            color,
            map: ringTexture,
            transparent: true,
            opacity: 0.48,
            side: THREE.DoubleSide,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
          });
          const ringInner = new THREE.Mesh(
            new THREE.RingGeometry(radius * 1.98, radius * 2.08, 128),
            ringMat,
          );
          ringInner.position.copy(pos);
          ringInner.rotation.x = Math.PI / 2.15 + (hash01(`${team.id}-ring-tilt`) - 0.5) * 0.3;
          const ringTwist = hash01(`${team.id}-ring-twist`) * Math.PI * 2;
          ringInner.rotation.z = ringTwist;
          contentRoot.add(ringInner);

          const planeTilt = (hash01(team.id) - 0.5) * 0.55;
          const planeTwist = hash01(`${team.id}-twist`) * Math.PI * 2;
          const label = makeLabelSprite(team.name, {
            scale: 0.95,
            sub: `Power ${formatPower(team.strength)}`,
          });
          label.position.set(pos.x, pos.y + radius + 16, pos.z);
          contentRoot.add(label);

          teamVisuals.push({
            corona,
            ringInner,
            ringTwist,
            baseCorona: coronaSize,
            spin: 0.08 + hash01(team.id) * 0.12,
          });

          nodeMap.set(team.id, {
            node: team,
            mesh: core,
            corona,
            ring: ringInner,
            label,
            planeTilt,
            planeTwist,
            baseRadius: radius,
            serieKey,
            levelId: String(team.graph_level_id || ""),
            position: pos.clone(),
          });
        });

        if (list.length) {
          serieCenters.set(serieKey, centerAccum.multiplyScalar(1 / list.length));
          const levelNode = levels.find((level) => resolveSerieKey(level) === serieKey || getLevelLetter(level.name) === serieKey);
          if (levelNode) {
            const c = serieCenters.get(serieKey);
            levelNode.x = c.x;
            levelNode.y = c.y + 90;
            levelNode.z = c.z;
            const levelLabel = makeLabelSprite(levelNode.name || `Serie ${serieKey}`, { scale: 1.15 });
            levelLabel.position.set(c.x, c.y + 110, c.z);
            contentRoot.add(levelLabel);
            const levelCore = new THREE.Mesh(
              new THREE.SphereGeometry(18, 20, 20),
              new THREE.MeshStandardMaterial({
                color,
                emissive: color,
                emissiveIntensity: 1.05,
                transparent: true,
                opacity: 0.95,
              }),
            );
            levelCore.position.set(c.x, c.y + 70, c.z);
            levelCore.userData = { node: levelNode, kind: "level" };
            contentRoot.add(levelCore);
            pickables.push(levelCore);
            nodeMap.set(levelNode.id, {
              node: levelNode,
              mesh: levelCore,
              serieKey,
              levelId: String(levelNode.raw_id || levelNode.graph_level_id || ""),
              position: levelCore.position.clone(),
            });
          }
        }
      });

      const teamIdByRaw = new Map(
        teams.map((team) => [String(team.raw_id || team.id?.replace?.(/^team-/, "") || ""), team]),
      );

      // Group players by team so orbit tiers look like a real star system.
      const playersByTeam = new Map();
      players.forEach((player) => {
        const key = String(player.team_raw_id || player.id || "");
        if (!playersByTeam.has(key)) playersByTeam.set(key, []);
        playersByTeam.get(key).push(player);
      });
      playersByTeam.forEach((list) => {
        list.sort((a, b) => Math.abs(Number(b.season_points) || 0) - Math.abs(Number(a.season_points) || 0));
      });

      players.forEach((player, playerIndex) => {
        const team =
          teamIdByRaw.get(String(player.team_raw_id || "")) ||
          teams.find((item) => item.id === `team-${player.team_raw_id}`) ||
          null;
        const teamEntry = team ? nodeMap.get(team.id) : null;
        const base = teamEntry?.position?.clone() || new THREE.Vector3(
          Math.cos(playerIndex) * 700,
          (Math.random() - 0.5) * 40,
          Math.sin(playerIndex) * 700,
        );
        const teamPlayers = playersByTeam.get(String(player.team_raw_id || player.id || "")) || [player];
        const rankInTeam = Math.max(0, teamPlayers.findIndex((item) => item.id === player.id));
        const teamCount = Math.max(1, teamPlayers.length);
        const seed = hash01(player.id || `${playerIndex}`);
        const seedB = hash01(`${player.id || playerIndex}-b`);
        // Golden-angle spread + tier by rank (inner stars = stronger).
        const ang = rankInTeam * GOLDEN_ANGLE + seed * 0.55 + (teamEntry?.planeTwist || 0);
        const tier = rankInTeam / teamCount;
        const teamCoreR = teamEntry?.baseRadius || 10;
        const pointsAbs = Math.abs(Number(player.season_points) || 0);
        const orbit = teamCoreR * 1.55 + 14 + tier * 28 + pointsAbs * 0.05 + seed * 6;
        const up = Number(player.season_points || 0) >= 0;
        const col = new THREE.Color(up ? "#7ec8ff" : "#ffc078");
        const discSize = 5.2 + Math.min(5.5, pointsAbs / 22);
        const mesh = new THREE.Sprite(
          new THREE.SpriteMaterial({
            map: softDiscTexture,
            color: col,
            transparent: true,
            opacity: 0.92,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
          }),
        );
        mesh.scale.set(discSize, discSize, 1);
        mesh.userData = { node: player, kind: "player" };
        contentRoot.add(mesh);
        // Invisible sphere for reliable ray pick (sprites pick poorly at grazing angles).
        const hitPad = new THREE.Mesh(
          new THREE.SphereGeometry(Math.max(discSize * 0.55, 7), 10, 10),
          new THREE.MeshBasicMaterial({
            transparent: true,
            opacity: 0,
            depthWrite: false,
          }),
        );
        hitPad.userData = { node: player, kind: "player" };
        contentRoot.add(hitPad);
        pickables.push(hitPad);

        const planeTilt = teamEntry?.planeTilt ?? (seed - 0.5) * 0.4;
        const planeTwist = teamEntry?.planeTwist ?? seedB * Math.PI * 2;
        const elev = (seedB - 0.5) * 0.22;
        const localX = Math.cos(ang) * orbit;
        const localZ = Math.sin(ang) * orbit;
        const cosT = Math.cos(planeTilt);
        const sinT = Math.sin(planeTilt);
        const cosW = Math.cos(planeTwist);
        const sinW = Math.sin(planeTwist);
        const rx = localX * cosW - localZ * sinW;
        const rz = localX * sinW + localZ * cosW;
        const ry = rz * sinT + elev * orbit * 0.35;
        const rz2 = rz * cosT;
        mesh.position.set(base.x + rx, base.y + ry, base.z + rz2);
        hitPad.position.copy(mesh.position);

        const playerLabel = makeLabelSprite(player.name || "VĐV", { scale: 0.55 });
        playerLabel.position.set(mesh.position.x, mesh.position.y + discSize * 0.55 + 6, mesh.position.z);
        playerLabel.visible = false;
        playerLabel.material.opacity = 0;
        contentRoot.add(playerLabel);

        playerOrbits.push({
          mesh,
          hitPad,
          label: playerLabel,
          base,
          ang,
          orbit,
          elev,
          planeTilt,
          planeTwist,
          discSize,
          baseOpacity: 0.92,
          speed: 0.09 + seed * 0.11 + (1 - tier) * 0.04,
          serieKey: teamEntry?.serieKey || resolveSerieKey(player),
          levelId: String(player.graph_level_id || teamEntry?.levelId || ""),
          node: player,
        });
        player.x = mesh.position.x;
        player.y = mesh.position.y;
        player.z = mesh.position.z;
        nodeMap.set(player.id, {
          node: player,
          mesh,
          hitPad,
          playerLabel,
          discSize,
          serieKey: teamEntry?.serieKey || resolveSerieKey(player),
          levelId: String(player.graph_level_id || teamEntry?.levelId || ""),
          position: mesh.position.clone(),
        });
      });

      links
        .filter((link) => link.type === "upcoming-match")
        .forEach((link) => {
          const sourceId = getLinkNodeId(link.source);
          const targetId = getLinkNodeId(link.target);
          const a = nodeMap.get(sourceId)?.position;
          const b = nodeMap.get(targetId)?.position;
          if (!a || !b) return;
          const geometry = new THREE.BufferGeometry().setFromPoints([a, b]);
          const line = new THREE.Line(
            geometry,
            new THREE.LineBasicMaterial({
              color: 0xffdd8a,
              transparent: true,
              opacity: 0.55,
            }),
          );
          line.userData = { link };
          battleRoot.add(line);
        });

      battleRoot.visible = Boolean(showScheduleLinks);
    };

    rebuildContent(graph);

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const clampCameraDistance = (target, position, minDist = 18, maxDist = 3600) => {
      const next = position.clone();
      const offset = next.clone().sub(target);
      const len = offset.length();
      if (len < 1e-4) {
        next.copy(target).add(new THREE.Vector3(0.4, 0.25, 1).normalize().multiplyScalar(minDist));
        return next;
      }
      if (len < minDist) offset.setLength(minDist);
      else if (len > maxDist) offset.setLength(maxDist);
      return target.clone().add(offset);
    };

    const cancelFly = () => {
      fly = null;
    };

    const flyTo = (target, position, durationMs = 1000, options = {}) => {
      const toTarget = target.clone();
      const minDist = options.minDistance ?? 18;
      const maxDist = options.maxDistance ?? 3600;
      const toPos = clampCameraDistance(toTarget, position, minDist, maxDist);
      controls.minDistance = Math.min(controls.minDistance, minDist);
      const dist = camera.position.distanceTo(toPos);
      const durationSec = Math.max(0.2, (durationMs || 600) / 1000);
      controls.autoRotate = false;
      if (durationMs <= 0 || dist < 1.5) {
        camera.position.copy(toPos);
        controls.target.copy(toTarget);
        controls.update();
        fly = null;
        return;
      }
      fly = {
        fromPos: camera.position.clone(),
        toPos,
        fromTarget: controls.target.clone(),
        toTarget,
        t: 0,
        duration: durationSec,
      };
    };

    // User drag/pinch/wheel must interrupt camera tween or OrbitControls feels dead.
    controls.addEventListener("start", cancelFly);

    const fitAll = (duration = 700) => {
      controls.minDistance = 12;
      controls.maxDistance = 4200;
      flyTo(new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 280, 1080), duration, {
        minDistance: 80,
        maxDistance: 4200,
      });
      controls.autoRotate = true;
    };

    const getNodeWorldPos = (nodeOrId) => {
      const id = typeof nodeOrId === "object" ? nodeOrId?.id : nodeOrId;
      const node = typeof nodeOrId === "object" ? nodeOrId : null;
      const entry = id ? nodeMap.get(id) : null;
      if (entry?.mesh?.position) return entry.mesh.position.clone();
      if (entry?.position) return entry.position.clone();
      const live = playerOrbits.find((item) => item.node?.id === id);
      if (live?.mesh?.position) return live.mesh.position.clone();
      if (node) {
        return new THREE.Vector3(Number(node.x) || 0, Number(node.y) || 0, Number(node.z) || 0);
      }
      return null;
    };

    const framePoints = (points, duration = 700, padding = 1.15) => {
      const valid = (points || []).filter(Boolean);
      if (!valid.length) return;
      const box = new THREE.Box3();
      valid.forEach((p) => box.expandByPoint(p));
      if (box.isEmpty()) return;
      const center = box.getCenter(new THREE.Vector3());
      const sizeVec = box.getSize(new THREE.Vector3());
      const span = Math.max(sizeVec.x, sizeVec.y * 1.1, sizeVec.z, 18);
      const fov = (camera.fov * Math.PI) / 180;
      const fit = (span * padding) / (2 * Math.tan(fov / 2));
      // Mobile needs a closer forced distance so the team fills the screen.
      const minClose = isMobile ? 22 : 26;
      const maxClose = isMobile ? 120 : 160;
      const distance = Math.max(minClose, Math.min(maxClose, fit * (isMobile ? 0.85 : 1)));
      // Always approach from a readable elevated angle (not current far-away dir only).
      const camDir = new THREE.Vector3(0.62, 0.38, 1).normalize();
      const destination = center.clone().addScaledVector(camDir, distance);
      controls.minDistance = 6;
      controls.maxDistance = 4200;
      controls.autoRotate = false;
      flyTo(center, destination, duration, { minDistance: 6, maxDistance: 2400 });
    };

    const focusNode = (node, duration = 500) => {
      if (!node) return;
      if (node.type === "team") {
        focusTeamSystem(node, duration);
        return;
      }
      const pos = getNodeWorldPos(node);
      if (!pos) return;
      const entry = nodeMap.get(node.id);
      if (entry) entry.position = pos.clone();
      node.x = pos.x;
      node.y = pos.y;
      node.z = pos.z;

      if (node.type === "player") {
        const teamId = node.team_raw_id ? `team-${node.team_raw_id}` : null;
        const teamPos = teamId ? getNodeWorldPos(teamId) : null;
        const points = [pos];
        if (teamPos) points.push(teamPos);
        framePoints(points, duration, 1.35);
        return;
      }

      const camDir = camera.position.clone().sub(controls.target);
      if (camDir.lengthSq() < 1) camDir.set(0.45, 0.35, 1);
      camDir.normalize();
      const distance = node.type === "level" ? 280 : 58;
      const destination = pos.clone().addScaledVector(camDir, distance);
      destination.y += node.type === "level" ? 40 : 10;
      flyTo(pos, destination, duration);
    };

    const focusTeamSystem = (teamNode, duration = 700) => {
      if (!teamNode) return;
      const teamPos = getNodeWorldPos(teamNode);
      if (!teamPos) return;
      const teamRaw = String(teamNode.raw_id || String(teamNode.id || "").replace(/^team-/, ""));
      const points = [teamPos];
      playerOrbits.forEach((item) => {
        const sameTeam =
          item.node?.team_raw_id != null
          && String(item.node.team_raw_id) === teamRaw;
        if (sameTeam && item.mesh?.position) points.push(item.mesh.position.clone());
      });
      // Keep frame tight around core + nearby orbit shell.
      const shell = isMobile ? 18 : 22;
      points.push(teamPos.clone().add(new THREE.Vector3(shell, 4, shell * 0.35)));
      points.push(teamPos.clone().add(new THREE.Vector3(-shell * 0.75, -3, -shell * 0.4)));
      points.push(teamPos.clone().add(new THREE.Vector3(0, shell * 0.45, -shell * 0.25)));
      framePoints(points, duration, isMobile ? 1.05 : 1.15);
    };

    const zoomBy = (factor = 0.8) => {
      const safeFactor = Math.max(0.35, Math.min(2.5, Number(factor) || 0.8));
      const offset = camera.position.clone().sub(controls.target);
      const nextLen = Math.max(controls.minDistance + 1, Math.min(controls.maxDistance - 1, offset.length() * safeFactor));
      offset.setLength(nextLen);
      const nextPos = controls.target.clone().add(offset);
      controls.autoRotate = false;
      flyTo(controls.target.clone(), nextPos, 280, {
        minDistance: controls.minDistance,
        maxDistance: controls.maxDistance,
      });
    };

    const focusSerie = (serieKeyOrLevelId, duration = 1200) => {
      const key = String(serieKeyOrLevelId || "");
      let center = serieCenters.get(key);
      if (!center) {
        for (const [, entry] of nodeMap) {
          if (entry.levelId === key || entry.serieKey === key) {
            center = serieCenters.get(entry.serieKey) || entry.position;
            break;
          }
        }
      }
      if (!center) {
        fitAll(duration);
        return;
      }
      flyTo(center.clone(), center.clone().add(new THREE.Vector3(220, 260, 520)), duration);
    };

    const applyFocusDim = (serieId, selectedId, focusIds) => {
      const serieFilter = String(serieId || "");
      const selected = String(selectedId || "");
      const focusSet = focusIds instanceof Set
        ? focusIds
        : Array.isArray(focusIds)
          ? new Set(focusIds)
          : focusIds && typeof focusIds === "object" && focusIds.size >= 0
            ? focusIds
            : null;
      const hasTeamFocus = Boolean(focusSet && focusSet.size > 0);

      nodeMap.forEach((entry) => {
        const id = entry.node?.id;
        const inFocus = !hasTeamFocus || focusSet.has(id);
        const matchSerie = !serieFilter
          || entry.levelId === serieFilter
          || entry.serieKey === serieFilter
          || String(entry.node.graph_level_id || "") === serieFilter;
        const isSelected = selected && id === selected;
        const active = hasTeamFocus ? inFocus : matchSerie;
        const opacity = !active ? 0.07 : isSelected || (hasTeamFocus && inFocus) ? 1 : 0.92;
        const focusedTeam = hasTeamFocus && inFocus && entry.node?.type === "team";

        if (entry.mesh?.material) {
          entry.mesh.material.transparent = true;
          entry.mesh.material.opacity = opacity;
          if ("emissiveIntensity" in entry.mesh.material) {
            const base = entry.node?.type === "level" ? 1.05 : entry.node?.type === "team" ? 0.72 : 0.55;
            entry.mesh.material.emissiveIntensity = !active ? 0.04 : isSelected || focusedTeam ? 1.15 : base;
          }
        }
        if (entry.corona?.material) {
          entry.corona.material.opacity = !active ? 0.04 : focusedTeam || isSelected ? 0.78 : 0.5;
        }
        if (entry.ring?.material) {
          entry.ring.material.opacity = !active ? 0.03 : focusedTeam || isSelected ? 0.55 : 0.36;
        }
        if (entry.ringOuter?.material) {
          entry.ringOuter.material.opacity = !active ? 0.02 : focusedTeam || isSelected ? 0.34 : 0.18;
        }
        if (entry.label) {
          entry.label.visible = Boolean(active);
          if (entry.label.material) entry.label.material.opacity = !active ? 0.08 : 1;
        }
        if (entry.playerLabel) {
          const showPlayerLabel = hasTeamFocus && inFocus && entry.node?.type === "player";
          entry.playerLabel.visible = showPlayerLabel;
          entry.playerLabel.material.opacity = showPlayerLabel ? 1 : 0;
        }
        if (entry.node?.type === "player" && entry.mesh?.isSprite && entry.discSize) {
          const scale = hasTeamFocus && inFocus ? entry.discSize * 1.28 : entry.discSize;
          entry.mesh.scale.set(scale, scale, 1);
        }
      });

      playerOrbits.forEach((item) => {
        const id = item.node?.id;
        const inFocus = !hasTeamFocus || focusSet.has(id);
        const matchSerie = !serieFilter
          || item.levelId === serieFilter
          || item.serieKey === serieFilter;
        const active = hasTeamFocus ? inFocus : matchSerie;
        item.mesh.material.transparent = true;
        item.mesh.material.opacity = active
          ? (hasTeamFocus && inFocus ? 1 : item.baseOpacity || 0.9)
          : 0.04;
        const size = item.discSize || 6;
        const scale = hasTeamFocus && inFocus ? size * 1.28 : size;
        item.mesh.scale.set(scale, scale, 1);
        if (item.label) {
          item.label.visible = hasTeamFocus && inFocus;
          item.label.material.opacity = hasTeamFocus && inFocus ? 1 : 0;
        }
      });
    };

    const clearFocus = () => {
      applyFocusDim("", "", null);
    };

    stateRef.current = {
      fitAll,
      focusNode,
      focusTeamSystem,
      focusSerie,
      zoomBy,
      resetCamera: () => {
        clearFocus();
        fitAll(700);
      },
      setAutoRotate: (enabled) => {
        controls.autoRotate = Boolean(enabled);
      },
      rebuildContent,
      applyFocusDim,
      clearFocus,
      setBattlesVisible: (visible) => {
        battleRoot.visible = Boolean(visible);
      },
      setSize: (nextWidth, nextHeight) => {
        const w = Math.max(1, nextWidth || 1);
        const h = Math.max(1, nextHeight || 1);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
        composer?.setSize(w, h);
        bloomPass?.setSize(w, h);
      },
    };

    let pointerDown = null;
    const pickAtClient = (clientX, clientY) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((clientX - rect.left) / Math.max(1, rect.width)) * 2 - 1;
      pointer.y = -((clientY - rect.top) / Math.max(1, rect.height)) * 2 + 1;
      raycaster.setFromCamera(pointer, camera);
      const hits = raycaster.intersectObjects(pickables, false);
      const hit = hits.find((item) => item.object?.userData?.node) || hits[0];
      if (hit?.object?.userData?.node && typeof onNodeClickRef.current === "function") {
        onNodeClickRef.current(hit.object.userData.node);
        return true;
      }
      if (typeof onBackgroundClickRef.current === "function") {
        onBackgroundClickRef.current();
      }
      return false;
    };

    const onPointerDown = (event) => {
      // Ignore multi-touch gestures (pinch/rotate handled by OrbitControls).
      if (event.isPrimary === false) return;
      if (typeof event.button === "number" && event.button !== 0) return;
      pointerDown = {
        x: event.clientX,
        y: event.clientY,
        t: performance.now(),
        pointerId: event.pointerId,
      };
    };
    const onPointerUp = (event) => {
      if (!pointerDown) return;
      if (pointerDown.pointerId != null && event.pointerId != null && pointerDown.pointerId !== event.pointerId) return;
      const dx = event.clientX - pointerDown.x;
      const dy = event.clientY - pointerDown.y;
      const moved = dx * dx + dy * dy;
      const elapsedMs = performance.now() - pointerDown.t;
      const start = pointerDown;
      pointerDown = null;
      // Mobile fingers drift more than mouse; allow a slightly larger tap slop.
      const tapSlop = isMobile ? 100 : 36;
      if (moved > tapSlop || elapsedMs > 850) return;
      pickAtClient(start.x, start.y);
    };
    const onPointerCancel = () => {
      pointerDown = null;
    };
    renderer.domElement.addEventListener("pointerdown", onPointerDown, { passive: true });
    renderer.domElement.addEventListener("pointerup", onPointerUp, { passive: true });
    renderer.domElement.addEventListener("pointercancel", onPointerCancel, { passive: true });
    // iOS Safari fallback when pointer events are flaky.
    const onTouchEnd = (event) => {
      if (!pointerDown || !event.changedTouches?.length) return;
      const touch = event.changedTouches[0];
      const dx = touch.clientX - pointerDown.x;
      const dy = touch.clientY - pointerDown.y;
      const moved = dx * dx + dy * dy;
      const elapsedMs = performance.now() - pointerDown.t;
      const start = pointerDown;
      pointerDown = null;
      if (moved > 120 || elapsedMs > 850) return;
      pickAtClient(start.x, start.y);
    };
    renderer.domElement.addEventListener("touchend", onTouchEnd, { passive: true });

    let frameId = 0;
    const animate = () => {
      if (disposed) return;
      frameId = window.requestAnimationFrame(animate);
      // getElapsedTime() already calls getDelta() and advances oldTime.
      // Calling getDelta() after it always returned ~0, so fly.t never advanced and
      // every frame snapped the camera back to the tween start — rotate/zoom looked broken.
      const delta = Math.min(0.05, Math.max(0, clock.getDelta() || 0));
      const elapsed = clock.elapsedTime;

      if (fly) {
        fly.t = Math.min(1, fly.t + delta / Math.max(0.05, fly.duration));
        const e = easeOutCubic(fly.t);
        camera.position.lerpVectors(fly.fromPos, fly.toPos, e);
        controls.target.lerpVectors(fly.fromTarget, fly.toTarget, e);
        if (fly.t >= 1) {
          camera.position.copy(fly.toPos);
          controls.target.copy(fly.toTarget);
          fly = null;
          controls.autoRotate = false;
        }
      }

      bgGalaxy.rotation.y = elapsed * 0.012;
      dust.rotation.y = elapsed * 0.008;
      bgGalaxy.material.opacity = 0.74 + Math.sin(elapsed * 1.2) * 0.1;
      sun.rotation.y = elapsed * 0.08;

      teamVisuals.forEach((visual) => {
        if (visual.ringInner) visual.ringInner.rotation.z = (visual.ringTwist || 0) + elapsed * visual.spin;
        if (visual.ringOuter) visual.ringOuter.rotation.z = -elapsed * visual.spin * 0.65 + 0.35;
        if (visual.corona && visual.baseCorona) {
          const pulse = 1 + Math.sin(elapsed * 1.4 + visual.spin * 8) * 0.06;
          const size = visual.baseCorona * pulse;
          visual.corona.scale.set(size, size, 1);
        }
      });

      playerOrbits.forEach((item) => {
        const ang = item.ang + elapsed * item.speed;
        const localX = Math.cos(ang) * item.orbit;
        const localZ = Math.sin(ang) * item.orbit;
        const cosT = Math.cos(item.planeTilt || 0);
        const sinT = Math.sin(item.planeTilt || 0);
        const cosW = Math.cos(item.planeTwist || 0);
        const sinW = Math.sin(item.planeTwist || 0);
        const rx = localX * cosW - localZ * sinW;
        const rz = localX * sinW + localZ * cosW;
        const bob = Math.sin(ang * 1.15 + elapsed * 0.35) * (item.orbit * 0.04);
        const ry = rz * sinT + (item.elev || 0) * item.orbit * 0.35 + bob;
        const rz2 = rz * cosT;
        const x = item.base.x + rx;
        const y = item.base.y + ry;
        const z = item.base.z + rz2;
        item.mesh.position.set(x, y, z);
        if (item.hitPad) item.hitPad.position.set(x, y, z);
        if (item.label) {
          const lift = (item.discSize || 6) * 0.55 + 6;
          item.label.position.set(x, y + lift, z);
        }
        const mapped = nodeMap.get(item.node?.id);
        if (mapped?.position) mapped.position.set(x, y, z);
      });

      controls.update();
      if (composer) composer.render();
      else renderer.render(scene, camera);
    };
    animate();

    stateRef.current.applyFocusDim(focusSerieId, selectedNodeId, focusNodeIds);
    stateRef.current.setBattlesVisible(showScheduleLinks);

    return () => {
      disposed = true;
      window.cancelAnimationFrame(frameId);
      controls.removeEventListener("start", cancelFly);
      renderer.domElement.removeEventListener("pointerdown", onPointerDown);
      renderer.domElement.removeEventListener("pointerup", onPointerUp);
      renderer.domElement.removeEventListener("pointercancel", onPointerCancel);
      renderer.domElement.removeEventListener("touchend", onTouchEnd);
      controls.dispose();
      clearGroup(contentRoot);
      clearGroup(battleRoot);
      [bgGalaxy, dust, farStars, sun, sunGlow].forEach((obj) => {
        obj.geometry?.dispose?.();
        if (obj.material) {
          if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose?.());
          else obj.material.dispose?.();
        }
      });
      starTexture.dispose();
      softDiscTexture.dispose();
      composer?.dispose?.();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
      stateRef.current = null;
    };
    // graph rebuilt via separate effect through stateRef
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    stateRef.current?.setSize?.(width, height);
  }, [width, height]);

  useEffect(() => {
    stateRef.current?.rebuildContent?.(graph);
    stateRef.current?.applyFocusDim?.(focusSerieId, selectedNodeId, focusNodeIds);
    stateRef.current?.setBattlesVisible?.(showScheduleLinks);
    // Chi fitAll lan dau load graph; neu dang focus doi thi giu camera.
    const hasFocus = focusNodeIds && typeof focusNodeIds.size === "number" && focusNodeIds.size > 0;
    if (hasFocus) return undefined;
    const timer = window.setTimeout(() => stateRef.current?.fitAll?.(900), 80);
    return () => window.clearTimeout(timer);
  }, [graphKey]);

  useEffect(() => {
    stateRef.current?.setBattlesVisible?.(showScheduleLinks);
  }, [showScheduleLinks]);

  useEffect(() => {
    stateRef.current?.applyFocusDim?.(focusSerieId, selectedNodeId, focusNodeIds);
  }, [focusSerieId, selectedNodeId, focusNodeIds]);

  return <div className="galaxy-home-canvas" ref={mountRef} style={{ width: "100%", height: "100%" }} />;
});

export default GalaxyHomeScene;
